/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { Button } from '@mui/material';
import { addClass } from '@grapecity/wijmo';
import { CellType } from '@grapecity/wijmo.grid';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import { findElectricSummary } from 'apis/ut/UtMatrixRegist';
import { UtMatrixDetail, UtMatrixRegistSearchCondition } from 'models/ut/UtMatrix';
import { hasRole } from 'utils/SessionUtil';
import { useLoading } from 'components/process/Loading';
import { DefaultZeroNumberCellTemplate } from 'components/grids/NumberCellRenderer';

/**
 * @desc 작성 > [전기요약] 탭 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.01   jshwang12       자연수(ex. 수량)를 제외한 숫자는 소수점 1자리까지 표현(2자리에서 반올림 처리)
 */
const ElectricSummaryContent = (props: any, ref) => {
  const {
    onSubmit,
    condition = {} as UtMatrixRegistSearchCondition,
    processData = [] as UtMatrixDetail[],
  } = props;
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { isLoading } = useLoading();
  const [rowData, setRowData] = useState<UtMatrixDetail[]>([]);
  const defaultGridHeight = 423;

  const isUtWriter = useMemo(() => {
    const p = processData.filter(
      (o) => o.prdnProcCd === condition?.prdnProcCd && o.eqclId === condition?.eqclId
    );
    return (p.length > 0 && p[0].utmWriteYn === 'Y') || hasRole('ADM');
  }, [processData, condition?.prdnProcCd, condition?.eqclId]);
  const isWrite = useMemo(() => {
    const p = processData.filter(
      (o) => o.prdnProcCd === condition?.prdnProcCd && o.eqclId === condition?.eqclId
    );
    return p.length > 0 && p[0].utmWrtProcProgStatCd === 'UTP02';
  }, [processData, condition?.prdnProcCd, condition?.eqclId]);
  const isWriteComplete = useMemo(() => {
    const p = processData.filter(
      (o) => o.prdnProcCd === condition?.prdnProcCd && o.eqclId === condition?.eqclId
    );
    return p.length > 0 && p[0].utmWrtProcProgStatCd === 'UTP03';
  }, [processData, condition?.prdnProcCd, condition?.eqclId]);

  useImperativeHandle(ref, () => ({
    searchElectricSummary: (condition) => {
      searchElectricSummary(condition);
    },
  }));

  const searchElectricSummary = (condition) => {
    findElectricSummary(condition).then((result) => {
      setRowData(result);
    });
  };

  const layoutDefinition = [
    {
      binding: 'no',
      header: String(t('com.label.NO', 'NO')),
      width: 40,
      align: 'center',
      cellTemplate: (grid) => grid.row._idx + 1,
    },
    {
      binding: 'prdnProcCdNm',
      header: String(t('ut.label.공정', '공정')),
      minWidth: 120,
      width: '*',
      align: 'center',
    },
    {
      binding: 'eqpPrdnLnQty',
      header: String(t('ut.label.라인수량', '라인수량')),
      minWidth: 120,
      width: '*',
    },
    {
      binding: 'vltgNvl',
      header: String(t('ut.label.전압', '전압')),
      align: 'center',
      minWidth: 120,
      width: '*',
    },
    {
      binding: 'elpwEqpQty',
      header: String(t('ut.label.설비수량', '설비수량')),
      align: 'right',
      minWidth: 120,
      width: '*',
      cellTemplate: DefaultZeroNumberCellTemplate,
    },
    {
      binding: 'od2EcNvl',
      header: String(t('ut.label.수용율 적용전', '수용율 적용전')),
      align: 'right',
      minWidth: 120,
      width: '*',
      cellTemplate: DefaultZeroNumberCellTemplate,
    },
    {
      binding: 'elecDmdRate',
      header: String(t('ut.label.수용율 적용 후', '수용율 적용 후')),
      align: 'right',
      minWidth: 120,
      width: '*',
      cellTemplate: DefaultZeroNumberCellTemplate,
    },
    {
      binding: 'diff',
      header: String(t('ut.label.차이', '차이')),
      align: 'right',
      minWidth: 120,
      width: '*',
      cellTemplate: (params) =>
        `${((params.item.od2EcNvl || 0) - (params.item.elecDmdRate || 0)).toLocaleString()}`,
    },
  ];

  const handleSubmit = () => {
    props?.onSubmit?.();
  };

  const handleModify = () => {
    props?.onModify?.();
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      if (binding) {
        if (item.vltgNvl === 'Sub Sum') {
          addClass(cell, 'rowTotal');
        } else if (item.vltgNvl === 'Total Sum') {
          addClass(cell, 'sumTotal');
        }
      }
    }
  });

  const onInitialized = (grid) => {
    grid.itemFormatter = onItemFormatter;
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ut.label.전기요약', '전기요약')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
          {condition?.utmNo && condition?.prdnProcCdNm && condition?.eqclNm && (
            <div className="info warning">
              {condition?.utmNo} / {condition?.prdnProcCdNm} / {condition?.eqclNm}
            </div>
          )}
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => searchElectricSummary(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={props?.isOpenCondition ? defaultGridHeight + 55 : defaultGridHeight + 120 + 39 + 55}
        isReadOnly={true}
        isSelector={false}
        allowPinning={false}
        excludeFilter={['prdnProcCdNm', 'eqpPrdnLnQty']}
        initialized={onInitialized}
      />
      <GlobalBtnGroup>
        {isWrite && isUtWriter && (
          <Button
            css={IconButton.button}
            className="save"
            disabled={isLoading}
            onClick={handleSubmit}
          >
            {t('com.button.제출', '제출')}
          </Button>
        )}
        {isWriteComplete && isUtWriter && (
          <Button
            css={IconButton.button}
            className="save"
            disabled={isLoading}
            onClick={handleModify}
          >
            {t('com.button.수정', '수정')}
          </Button>
        )}
      </GlobalBtnGroup>
    </>
  );
};

export default React.forwardRef(ElectricSummaryContent);
